import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import AudioButton from '../components/AudioButton';
import { getImageUrl } from '../config';
import { FiBook, FiLayers, FiHash, FiType, FiArrowRight, FiHome, FiAward, FiUsers, FiUser, FiKey, FiStar } from 'react-icons/fi';

const Nouns = ({ updateProgress, setCurrentSection }) => {
  const [activeTab, setActiveTab] = useState('gender');

  useEffect(() => {
    setCurrentSection('nouns');
    updateProgress('nouns');
  }, []);

  const nounExamples = {
    gender: [
      {
        id: 'bio-gender',
        title: 'Biological Gender',
        masculine: { text: 'ወንድ (wend)', audioId: 'wend', image: getImageUrl('boy.jpg') },
        feminine: { text: 'ሴት (set)', audioId: 'set', image: getImageUrl('girl.jpg') },
        meaning: 'boy / girl',
        cultural: 'Natural biological distinction',
        icon: FiUsers,
        color: 'bg-blue-50 border-blue-200',
        accentColor: 'text-blue-600',
        visualBg: 'bg-blue-100/50'
      },
      {
        id: 'parent-gender',
        title: 'Parental Roles',
        masculine: { text: 'አባት (abat)', audioId: 'abat', image: getImageUrl('father.jpg') },
        feminine: { text: 'እናት (enat)', audioId: 'enat', image: getImageUrl('mother.jpg') },
        meaning: 'father / mother',
        cultural: 'Family hierarchy markers',
        icon: FiHome,
        color: 'bg-purple-50 border-purple-200',
        accentColor: 'text-purple-600',
        visualBg: 'bg-purple-100/50'
      },
      {
        id: 'honorific-gender',
        title: 'Professional Titles',
        masculine: { text: 'መምህር (memhir)', audioId: 'memhir', image: getImageUrl('male-teacher.jpg') },
        feminine: { text: 'መምህርት (memhirit)', audioId: 'memhirit', image: getImageUrl('female-teacher.jpg') },
        meaning: 'teacher (m) / teacher (f)',
        cultural: 'Gendered job titles',
        icon: FiAward,
        color: 'bg-indigo-50 border-indigo-200',
        accentColor: 'text-indigo-600',
        visualBg: 'bg-indigo-100/50'
      }
    ],
    number: [
      {
        singular: 'ልጅ (lij)',
        plural: 'ልጆች (lijoch)',
        type: '-och suffix',
        meaning: 'child / children',
        icon: FiUser,
        pluralCount: 3,
        visual: 'One child vs Group',
        color: 'bg-amber-50 border-amber-200',
        accentColor: 'text-amber-600',
        audioIdSingular: 'lij',
        audioIdPlural: 'lijoch'
      },
      {
        singular: 'ሰው (sew)',
        plural: 'ሰዎች (sewoch)',
        type: 'broken plural',
        meaning: 'person / people',
        icon: FiUsers,
        pluralCount: 4,
        visual: 'Individual vs Crowd',
        color: 'bg-orange-50 border-orange-200',
        accentColor: 'text-orange-600',
        audioIdSingular: 'sew',
        audioIdPlural: 'sewoch'
      },
      {
        singular: 'ቤት (bet)',
        plural: 'ቤቶች (betoch)',
        type: '-och suffix',
        meaning: 'house / houses',
        icon: FiHome,
        pluralCount: 3,
        visual: 'Single house vs Village',
        color: 'bg-emerald-50 border-emerald-200',
        accentColor: 'text-emerald-600',
        audioIdSingular: 'bet',
        audioIdPlural: 'betoch'
      },
      {
        singular: 'ቁልፍ (kulf)',
        plural: 'ቁልፎች (kulfoch)',
        type: '-och suffix',
        meaning: 'key / keys',
        icon: FiKey,
        pluralCount: 2,
        visual: 'One key vs Bunch',
        color: 'bg-cyan-50 border-cyan-200',
        accentColor: 'text-cyan-600',
        audioIdSingular: 'kulf',
        audioIdPlural: 'kulfoch'
      }
    ],
    definiteness: [
      {
        indefinite: 'ቤት (bet)',
        definite: 'ቤቱ (betu)',
        meaning: 'a house / the house',
        marker: '-u suffix',
        icon: FiHome,
        gender: 'Masculine',
        visual: 'Generic house vs Specific house',
        color: 'bg-rose-50 border-rose-200',
        accentColor: 'text-rose-600',
        audioIdIndefinite: 'bet',
        audioIdDefinite: 'betu'
      },
      {
        indefinite: 'ሴት (set)',
        definite: 'ሴቷ (setwa)',
        meaning: 'a woman / the woman',
        marker: '-wa suffix',
        icon: FiUsers,
        gender: 'Feminine',
        visual: 'Generic woman vs Specific woman',
        color: 'bg-teal-50 border-teal-200',
        accentColor: 'text-teal-600',
        audioIdIndefinite: 'set',
        audioIdDefinite: 'setwa'
      }
    ]
  };

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`relative group flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all duration-300 overflow-hidden ${activeTab === id
        ? 'bg-green-600 text-white shadow-lg shadow-green-200 scale-105'
        : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-gray-100'
        }`}
    >
      <div className={`absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ${activeTab === id ? 'block' : 'hidden'}`}></div>
      <SafeIcon icon={icon} className={`text-lg ${activeTab === id ? 'animate-bounce' : ''}`} />
      <span className="relative z-10">{label}</span>
    </button>
  );

  return (
    <PageLayout
      title="Nouns: Gender & Number"
      subtitle="Understand the building blocks of Amharic sentences. Learn how gender, plurality, and definiteness shape the language."
      icon={FiBook}
      prevLink="/"
      prevLabel="Dashboard"
      nextLink="/pronouns"
      nextLabel="Pronouns"
    >
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-4 mb-10 p-2 bg-gray-50/50 rounded-3xl w-fit">
        <TabButton id="gender" label="Gender" icon={FiType} />
        <TabButton id="number" label="Number" icon={FiHash} />
        <TabButton id="definiteness" label="Definiteness" icon={FiLayers} />
      </div>

      {/* Content Area */}
      <div className="min-h-[500px]">

        {/* GENDER TAB */}
        {activeTab === 'gender' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Gender in Amharic</h3>
              <p className="text-gray-500 text-lg">
                Amharic nouns have masculine and feminine genders. This distinction affects grammar, adjectives, and pronouns.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {nounExamples.gender.map((item, index) => (
                <div
                  key={index}
                  className={`group relative flex flex-col h-full bg-white rounded-[2rem] border-2 border-b-[8px] ${item.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl`}
                >
                  {/* Visual Header with Images - OPTIMIZED: object-contain with backgrounds */}
                  <div className="relative h-48 mb-6 rounded-3xl overflow-hidden bg-gray-50 border-2 border-white/50 shadow-inner flex">
                    {/* Masculine Side */}
                    <div className="w-1/2 h-full relative border-r border-gray-200 flex items-center justify-center bg-blue-50/30">
                      <img src={item.masculine.image} alt={item.masculine.text} className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute bottom-2 left-2 right-2 text-center pointer-events-none">
                        <span className="bg-blue-500/90 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm backdrop-blur-sm">
                          Masc
                        </span>
                      </div>
                    </div>
                    {/* Feminine Side */}
                    <div className="w-1/2 h-full relative flex items-center justify-center bg-pink-50/30">
                      <img src={item.feminine.image} alt={item.feminine.text} className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute bottom-2 left-2 right-2 text-center pointer-events-none">
                        <span className="bg-pink-500/90 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm backdrop-blur-sm">
                          Fem
                        </span>
                      </div>
                    </div>

                    {/* Top Badge */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-gray-700 border border-white/50 shadow-sm z-10 whitespace-nowrap">
                      {item.title}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4 flex-1">
                    {/* Masculine Row */}
                    <div className="bg-white rounded-2xl p-3 border border-gray-100 flex items-center justify-between shadow-sm group-hover:border-blue-200 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-blue-100 bg-gray-50 flex items-center justify-center">
                          <img src={item.masculine.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <span className="text-[10px] text-blue-500 font-bold uppercase tracking-widest block">Masculine</span>
                          <span className="font-amharic text-lg font-bold text-gray-800">{item.masculine.text.split(' ')[0]}</span>
                        </div>
                      </div>
                      <AudioButton audioId={item.masculine.audioId} text={item.masculine.text.split(' ')[0]} />
                    </div>

                    {/* Feminine Row */}
                    <div className="bg-white rounded-2xl p-3 border border-gray-100 flex items-center justify-between shadow-sm group-hover:border-pink-200 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-pink-100 bg-gray-50 flex items-center justify-center">
                          <img src={item.feminine.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <span className="text-[10px] text-pink-500 font-bold uppercase tracking-widest block">Feminine</span>
                          <span className="font-amharic text-lg font-bold text-gray-800">{item.feminine.text.split(' ')[0]}</span>
                        </div>
                      </div>
                      <AudioButton audioId={item.feminine.audioId} text={item.feminine.text.split(' ')[0]} />
                    </div>

                    <div className="pt-2 border-t border-gray-200/50 mt-2">
                      <p className="text-center text-sm font-medium text-gray-500">{item.meaning}</p>
                      <p className="text-center text-xs text-gray-400 mt-1 italic">{item.cultural}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NUMBER TAB */}
        {activeTab === 'number' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Plural Formation</h3>
              <p className="text-gray-500 text-lg">
                Most nouns become plural by adding the suffix <span className="font-mono bg-green-100 text-green-700 px-2 py-0.5 rounded-lg">-och</span>.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {nounExamples.number.map((item, index) => (
                <div
                  key={index}
                  className={`group relative flex flex-col bg-white rounded-[2rem] border-2 border-b-[8px] ${item.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl`}
                >
                  {/* Visual Comparison Area */}
                  <div className="relative h-48 mb-6 rounded-3xl overflow-hidden bg-white/60 border-2 border-white/50 shadow-inner flex items-center justify-center">
                    <div className="flex items-center gap-8 md:gap-12 w-full justify-center px-4">
                      {/* Singular Side */}
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-2xl bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 transition-transform">
                          <SafeIcon icon={item.icon} className={`text-3xl ${item.accentColor}`} />
                        </div>
                        <span className="text-xs font-bold text-gray-400 uppercase">One</span>
                      </div>

                      {/* Arrow & Suffix */}
                      <div className="flex flex-col items-center text-gray-300">
                        <SafeIcon icon={FiArrowRight} className="text-2xl mb-1" />
                        <span className={`text-xs font-bold px-2 py-1 rounded-lg ${item.color.replace('bg-', 'bg-white ')} border border-gray-200`}>
                          {item.type.includes('broken') ? 'Irregular' : '+och'}
                        </span>
                      </div>

                      {/* Plural Side */}
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-2xl bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 transition-transform relative">
                          {/* Generated icons for plural */}
                          <div className="grid grid-cols-2 gap-1">
                            {[...Array(Math.min(4, item.pluralCount))].map((_, i) => (
                              <SafeIcon key={i} icon={item.icon} className={`text-sm ${item.accentColor}`} />
                            ))}
                          </div>
                        </div>
                        <span className="text-xs font-bold text-gray-400 uppercase">Many</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-2xl border border-gray-100 text-center group-hover:border-gray-200 transition-all">
                      <div className="text-xs text-gray-400 font-bold uppercase mb-1">Singular</div>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="font-amharic text-xl font-bold text-gray-800">{item.singular.split(' ')[0]}</div>
                        <AudioButton audioId={item.audioIdSingular} text={item.singular.split(' ')[0]} small />
                      </div>
                      <div className="text-xs text-gray-500">{item.singular.split(' ')[1]}</div>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-gray-100 text-center group-hover:border-gray-200 transition-all">
                      <div className="text-xs text-gray-400 font-bold uppercase mb-1">Plural</div>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="font-amharic text-xl font-bold text-gray-800">{item.plural.split(' ')[0]}</div>
                        <AudioButton audioId={item.audioIdPlural} text={item.plural.split(' ')[0]} small />
                      </div>
                      <div className="text-xs text-gray-500">{item.plural.split(' ')[1]}</div>
                    </div>
                  </div>

                  <div className="mt-4 text-center">
                    <p className="text-sm font-medium text-gray-600 bg-white/50 py-2 rounded-xl inline-block px-6">
                      {item.meaning}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DEFINITENESS TAB */}
        {activeTab === 'definiteness' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Definiteness (The)</h3>
              <p className="text-gray-500 text-lg">
                Amharic adds a suffix to the end of a noun to make it definite (like adding "the" in English).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {nounExamples.definiteness.map((item, index) => (
                <div
                  key={index}
                  className={`group relative flex flex-col bg-white rounded-[2rem] border-2 border-b-[8px] ${item.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl`}
                >
                  {/* Visual Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`px-4 py-2 rounded-xl bg-white border border-gray-100 font-bold text-sm ${item.accentColor}`}>
                      {item.gender}
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                      <span>Suffix:</span>
                      <span className="bg-gray-800 text-white px-2 py-1 rounded-lg">{item.marker.split(' ')[0]}</span>
                    </div>
                  </div>

                  {/* Transformation Visual */}
                  <div className="relative h-40 mb-6 rounded-3xl overflow-hidden bg-white/60 border-2 border-white/50 shadow-inner flex items-center justify-center">
                    <div className="flex items-center gap-8">
                      {/* Indefinite */}
                      <div className="text-center opacity-50 group-hover:opacity-100 transition-opacity">
                        <SafeIcon icon={item.icon} className={`text-4xl text-gray-400 mb-2`} />
                        <div className="text-xs font-bold text-gray-400">A {item.meaning.split(' / ')[0].split(' ')[1]}</div>
                      </div>

                      <SafeIcon icon={FiArrowRight} className={`text-2xl ${item.accentColor} animate-pulse`} />

                      {/* Definite */}
                      <div className="text-center transform group-hover:scale-110 transition-transform">
                        <div className="relative">
                          <SafeIcon icon={item.icon} className={`text-5xl ${item.accentColor} mb-2`} />
                          <div className="absolute -top-1 -right-1 bg-yellow-400 text-white rounded-full p-1 shadow-sm">
                            <SafeIcon icon={FiStar} className="text-[10px]" />
                          </div>
                        </div>
                        <div className={`text-xs font-bold ${item.accentColor}`}>The {item.meaning.split(' / ')[0].split(' ')[1]}</div>
                      </div>
                    </div>
                  </div>

                  {/* Text Comparison */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100">
                      <span className="text-gray-500 font-medium">Indefinite</span>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="font-amharic text-xl font-bold text-gray-800">{item.indefinite.split(' ')[0]}</div>
                          <AudioButton audioId={item.audioIdIndefinite} text={item.indefinite.split(' ')[0]} small />
                        </div>
                        <div className="text-xs text-gray-400">{item.indefinite.split(' ')[1]}</div>
                      </div>
                    </div>

                    <div className={`flex items-center justify-between p-4 rounded-2xl border-2 ${item.color.replace('bg-', 'bg-white ').replace('border-', 'border-')}`}>
                      <span className={`font-bold ${item.accentColor}`}>Definite</span>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className={`font-amharic text-xl font-bold ${item.accentColor}`}>
                            {item.definite.split(' ')[0]}
                          </div>
                          <AudioButton audioId={item.audioIdDefinite} text={item.definite.split(' ')[0]} small />
                        </div>
                        <div className="text-xs text-gray-500">{item.definite.split(' ')[1]}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Nouns;